import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { Save as SaveIcon, Send as SendIcon } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { toast } from 'react-toastify';
import { Link as RouterLink } from 'react-router-dom';
import { Link } from '@mui/material';
import api from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import RequiredBadge from '../../components/Common/RequiredBadge';
import { useAuth } from '../../contexts/AuthContext';
import { SELF_EVALUATION_PATH } from '../../utils/routes';

interface EvaluationGoal {
  goal_id: string;
  approval_status: 'draft' | 'pending_approval' | 'approved' | 'rejected';
  rejection_reason?: string | null;
  version: number;
  revision_phase: string;
}

interface GoalItem {
  item_detail_id: string;
  item_id: string;
  category_number: number;
  category_name: string;
  item_number: string;
  item_description: string;
  weight: number;
  requires_goal: boolean;
  is_free_item: boolean;
  target_description: string | null;
  achievement_criteria: string | null;
  detailed_definition: string | null;
  evaluation_points: string[] | null;
}

interface GoalCategory {
  category_number: number;
  category_name: string;
  total_weight: number;
  items: GoalItem[];
}

interface GoalApproval {
  approval_id: string;
  approver_id: string;
  approver_name: string;
  evaluation_category: string;
  status: string;
  comment: string | null;
  is_proxy: boolean;
  proxy_by_name: string | null;
}

interface ApprovalProgress {
  total: number;
  approved: number;
  rejected: number;
  allApproved: boolean;
}

const statusLabel: Record<string, { label: string; color: 'default' | 'warning' | 'success' | 'error' | 'info' }> = {
  draft: { label: '下書き', color: 'default' },
  pending_approval: { label: '承認待ち', color: 'warning' },
  approved: { label: '承認済み（目標確定）', color: 'success' },
  rejected: { label: '差し戻し', color: 'error' },
};

const GoalSettingPage: React.FC = () => {
  const { periodStatus } = useAuth();
  const [periodId, setPeriodId] = useState(periodStatus?.activePeriodId || '');
  const [goal, setGoal] = useState<EvaluationGoal | null>(null);
  const [categories, setCategories] = useState<GoalCategory[]>([]);
  const [inputs, setInputs] = useState<Record<string, { target: string; criteria: string }>>({});
  const [canEditGoals, setCanEditGoals] = useState(false);
  const [formEditable, setFormEditable] = useState(false);
  const [requiresGoalItems, setRequiresGoalItems] = useState(0);
  const [totalWeight, setTotalWeight] = useState(0);
  const [approvals, setApprovals] = useState<GoalApproval[]>([]);
  const [approvalProgress, setApprovalProgress] = useState<ApprovalProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadGoals = async (pid: string) => {
    if (!pid) return;
    setLoading(true);
    try {
      const res = await api.get(`/evaluation-goals/period/${pid}/items`);
      const data = res.data.data;
      setGoal(data.goal);
      setCategories(data.categories || []);
      setCanEditGoals(data.canEditGoals);
      setFormEditable(data.formEditable);
      setRequiresGoalItems(data.requires_goal_items || 0);
      setTotalWeight(data.total_weight || 0);
      setApprovals(data.approvals || []);
      setApprovalProgress(data.approval_progress || null);

      const nextInputs: Record<string, { target: string; criteria: string }> = {};
      for (const category of data.categories || []) {
        for (const item of category.items) {
          nextInputs[item.item_detail_id] = {
            target: item.target_description || '',
            criteria: item.achievement_criteria || '',
          };
        }
      }
      setInputs(nextInputs);
    } catch (e) {
      toast.error(extractErrorMessage(e, '目標の取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (periodStatus?.activePeriodId) {
      setPeriodId(periodStatus.activePeriodId);
    }
  }, [periodStatus?.activePeriodId]);

  useEffect(() => {
    if (periodId) loadGoals(periodId);
  }, [periodId]);

  const allItems = categories.flatMap((category) => category.items);

  const missingRequired = allItems.filter((item) => {
    if (!item.requires_goal) return false;
    const input = inputs[item.item_detail_id];
    return !input?.target.trim() || !input?.criteria.trim();
  });

  const buildPayload = () => ({
    items: allItems.map((item) => ({
      item_detail_id: item.item_detail_id,
      target_description: inputs[item.item_detail_id]?.target ?? '',
      achievement_criteria: inputs[item.item_detail_id]?.criteria ?? '',
    })),
  });

  const handleSaveDraft = async () => {
    if (!periodId) return;
    setSaving(true);
    try {
      await api.put(`/evaluation-goals/period/${periodId}/items`, buildPayload());
      toast.success('目標を保存しました');
      await loadGoals(periodId);
    } catch (e) {
      toast.error(extractErrorMessage(e, '保存に失敗しました'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!periodId) return;
    setSaving(true);
    try {
      await api.put(`/evaluation-goals/period/${periodId}/items`, buildPayload());
      await api.post(`/evaluation-goals/period/${periodId}/submit`);
      toast.success('上長への承認申請を送信しました');
      await loadGoals(periodId);
    } catch (e) {
      toast.error(extractErrorMessage(e, '申請に失敗しました'));
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (itemDetailId: string, field: 'target' | 'criteria', value: string) => {
    setInputs((prev) => ({
      ...prev,
      [itemDetailId]: {
        target: field === 'target' ? value : prev[itemDetailId]?.target ?? '',
        criteria: field === 'criteria' ? value : prev[itemDetailId]?.criteria ?? '',
      },
    }));
  };

  if (loading && categories.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const chip = goal ? statusLabel[goal.approval_status] : statusLabel.draft;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Typography variant="h4" gutterBottom>
          目標設定
        </Typography>
        {goal && <Chip label={chip.label} color={chip.color} />}
      </Box>
      <Typography color="text.secondary" paragraph>
        評価項目ごとに目標と達成基準を設定し、上長の承認を受けてから自己評価に進みます。一度承認されると目標は確定し、編集できません。
      </Typography>

      {categories.length === 0 ? (
        <Alert severity="warning">
          評価項目がまだ生成されていません。評価ルートの設定が完了しているか、管理者にご確認ください。
        </Alert>
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  評価項目
                </Typography>
                <Typography variant="h5">{allItems.length} 項目</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  配点合計
                </Typography>
                <Typography variant="h5">{totalWeight} 点</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  入力が必要な項目
                </Typography>
                <Typography variant="h5">{requiresGoalItems} 項目</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  未入力
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: missingRequired.length === 0 ? 'success.main' : 'error.main',
                    fontWeight: 'bold',
                  }}
                >
                  {missingRequired.length} 項目
                </Typography>
              </Box>
            </Box>
          </Paper>

          {!canEditGoals && (
            <Alert severity="info" sx={{ mb: 2 }}>
              現在は目標設定期間・中期レビュー期間外のため、目標の編集・承認申請はできません。
            </Alert>
          )}

          {goal?.approval_status === 'approved' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              目標は確定済みです（編集不可）。
              <Link component={RouterLink} to={SELF_EVALUATION_PATH} sx={{ ml: 1 }}>
                自己評価へ進む
              </Link>
            </Alert>
          )}

          {goal?.approval_status === 'pending_approval' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              上長の承認待ちです。承認されるまで自己評価は提出できません。
              {approvalProgress && approvalProgress.total > 1 && (
                <strong>
                  {' '}
                  {approvalProgress.total}名の上長全員の承認が必要です（現在 {approvalProgress.approved}名が承認済み）。
                </strong>
              )}
            </Alert>
          )}

          {approvals.length > 0 && goal?.approval_status !== 'draft' && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                承認の状況
                {approvalProgress && (
                  <Chip
                    size="small"
                    sx={{ ml: 1 }}
                    color={approvalProgress.allApproved ? 'success' : 'default'}
                    label={`${approvalProgress.approved} / ${approvalProgress.total} 名`}
                  />
                )}
              </Typography>
              {approvals.map((approval) => (
                <Typography key={approval.approval_id} variant="body2" color="text.secondary">
                  {approval.approver_name}
                  （{approval.evaluation_category === 'manager' ? '上長' : '部門長'}）:{' '}
                  {approval.status === 'approved'
                    ? '承認済み'
                    : approval.status === 'rejected'
                      ? '差し戻し'
                      : '未承認'}
                  {approval.is_proxy && approval.proxy_by_name
                    ? `（${approval.proxy_by_name} が代行）`
                    : ''}
                </Typography>
              ))}
            </Paper>
          )}

          {goal?.approval_status === 'rejected' && goal.rejection_reason && (
            <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
              差し戻し理由: {goal.rejection_reason}
            </Alert>
          )}

          {formEditable && missingRequired.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              入力が必要な項目が {missingRequired.length} 件あります（
              {missingRequired.map((item) => item.item_number).join('、')}
              ）。すべて入力すると承認申請できます。
            </Alert>
          )}

          {categories.map((category) => (
            <Accordion key={category.category_number} defaultExpanded sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6">
                    観点{category.category_number}: {category.category_name}
                  </Typography>
                  <Chip label={`${category.total_weight}点`} color="primary" size="small" />
                  <Chip label={`${category.items.length}項目`} variant="outlined" size="small" />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {category.items.map((item) => {
                    const input = inputs[item.item_detail_id] || { target: '', criteria: '' };
                    const isMissing =
                      item.requires_goal && (!input.target.trim() || !input.criteria.trim());

                    return (
                      <Card
                        key={item.item_detail_id}
                        variant="outlined"
                        sx={{
                          borderColor: isMissing ? 'error.main' : undefined,
                          borderWidth: isMissing ? 2 : 1,
                        }}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {item.item_number}. {item.item_description}
                            </Typography>
                            <Chip label={`${item.weight}点`} size="small" />
                            {item.is_free_item && (
                              <Chip label="フリー項目" size="small" color="info" />
                            )}
                            {item.requires_goal ? (
                              <Chip label="入力必須" size="small" color="warning" />
                            ) : (
                              <Chip label="任意" size="small" variant="outlined" />
                            )}
                          </Box>

                          {(item.detailed_definition ||
                            (item.evaluation_points && item.evaluation_points.length > 0)) && (
                            <Box sx={{ mb: 2, bgcolor: 'action.hover', p: 1.5, borderRadius: 1 }}>
                              {item.detailed_definition && (
                                <Typography variant="body2" color="text.secondary" paragraph>
                                  {item.detailed_definition}
                                </Typography>
                              )}
                              {item.evaluation_points && item.evaluation_points.length > 0 && (
                                <>
                                  <Typography variant="caption" color="text.secondary">
                                    評価のポイント
                                  </Typography>
                                  <Box component="ul" sx={{ m: 0, pl: 3 }}>
                                    {item.evaluation_points.map((point, index) => (
                                      <Typography
                                        key={index}
                                        component="li"
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {point}
                                      </Typography>
                                    ))}
                                  </Box>
                                </>
                              )}
                            </Box>
                          )}

                          <Divider sx={{ mb: 2 }} />

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              目標設定（SMART原則）
                            </Typography>
                            {item.requires_goal && <RequiredBadge />}
                          </Box>
                          <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={input.target}
                            onChange={(e) => handleChange(item.item_detail_id, 'target', e.target.value)}
                            disabled={!formEditable || saving}
                            sx={{ mb: 2 }}
                          />

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              達成基準
                            </Typography>
                            {item.requires_goal && <RequiredBadge />}
                          </Box>
                          <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={input.criteria}
                            onChange={(e) =>
                              handleChange(item.item_detail_id, 'criteria', e.target.value)
                            }
                            disabled={!formEditable || saving}
                          />
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}

          <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={handleSaveDraft}
              disabled={!formEditable || saving}
            >
              下書き保存
            </Button>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleSubmit}
              disabled={!formEditable || saving || missingRequired.length > 0}
            >
              承認申請
            </Button>
          </Box>
        </>
      )}
    </Container>
  );
};

export default GoalSettingPage;
